import { useNavigate } from "react-router-dom";
import { ArrowRight, Mic, Sparkles } from "lucide-react";
import { UsersFour, UsersTwo } from "@/components/PeopleIcons";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/a72034c2-4f95-4f18-be74-1d29b1871fdc/images/5b1f4d3e50ecd6db3d52abe7217d008969f9da796848a6f68dbbe91e8e6b1992.png";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F5EEDB]">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Refugium" className="w-10 h-10 rounded-full shadow-sm" />
          <span className="font-heading text-xl text-[#5B3E2E] tracking-tight">Refugium</span>
        </div>
        <Button
          data-testid="landing-login-btn"
          variant="outline"
          onClick={() => navigate("/login")}
          className="rounded-full border-[#5B3E2E]/20 text-[#5B3E2E] hover:bg-[#5B3E2E]/5 text-sm"
        >
          Iniciar sesión
        </Button>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-16 md:pt-16 md:pb-24 flex flex-col md:flex-row items-start md:items-center gap-10 md:gap-16">
        <div className="flex-1 animate-fade-in">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#5B3E2E] tracking-tight leading-[1.1] mb-8">
            Un espacio donde{" "}
            <span className="text-[#E48D57]">acompañar</span> y ser{" "}
            <span className="text-[#6B9070]">acompañad@</span>
          </h1>
          <div className="mb-8 md:hidden">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E48D57]/5 rounded-[2rem] -rotate-3" />
              <img src={HERO_IMG} alt="Refugium" className="relative w-full rounded-3xl shadow-[0_20px_60px_rgba(91,62,46,0.15)]" />
            </div>
          </div>
          <p className="text-base leading-relaxed text-[#5B3E2E]/60 mb-8 max-w-lg text-justify">
            Refugium es una plataforma online de acompañamiento emocional. Un lugar donde encontrar apoyo, pero también donde prestarlo; una comunidad que te sostiene y a la que tú también puedes sostener.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="landing-register-btn"
              onClick={() => navigate("/register")}
              className="bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full px-8 py-3 font-medium hover:-translate-y-0.5 transition-all shadow-[0_4px_16px_rgba(217,120,51,0.3)]"
            >
              Únete a Refugium
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/login")}
              className="rounded-full border-[#5B3E2E]/15 text-[#5B3E2E]/70 hover:bg-[#5B3E2E]/5"
            >
              Ya tengo cuenta
            </Button>
          </div>
        </div>
        <div className="flex-1 justify-center animate-slide-up hidden md:flex">
          <div className="relative">
            <div className="absolute -inset-4 bg-[#E48D57]/5 rounded-[2rem] -rotate-3" />
            <img src={HERO_IMG} alt="Refugium" className="relative w-full max-w-sm rounded-3xl shadow-[0_20px_60px_rgba(91,62,46,0.15)]" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#5B3E2E] mb-3">¿Cómo funciona?</h2>
          <p className="text-[#5B3E2E]/50 text-sm max-w-md mx-auto">Cuatro pilares para una comunidad que cuida y acompaña</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: UsersFour,
              title: "Acompañamiento grupal",
              desc: "Crea tu propia sala para ser acompañad@ o únete a otras salas para acompañar a otros miembros.",
              color: "#E48D57",
            },
            {
              icon: Sparkles,
              title: "Contenido exclusivo",
              desc: "Un panel con entrevistas, reflexiones y materiales creados para los miembros de la comunidad.",
              color: "#E48D57",
            },
            {
              icon: Mic,
              title: "Voces Refugium",
              desc: "Si crees que tu historia puede inspirar, compártela en el formato que elijas: vídeo, audio o texto.",
              color: "#C44A3A",
              badge: "Voluntario",
              extra: "Escríbenos o rellena el formulario dentro de la app.",
            },
            {
              icon: UsersTwo,
              title: "Acompañamiento individualizado",
              desc: "Sesiones uno a uno de 30 minutos por llamada de teléfono (o de WhatsApp si estás fuera de España), cuando necesites un espacio más íntimo.",
              color: "#C08B4C",
              badge: "Bajo cita",
              extra: "Servicio complementario · 29,90 € por sesión (IVA incluido).",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="relative rounded-3xl p-7 border border-[#5B3E2E]/8 animate-slide-up hover:-translate-y-1 transition-all duration-300 shadow-[0_2px_16px_rgba(91,62,46,0.04)] hover:shadow-[0_8px_30px_rgba(91,62,46,0.08)] overflow-hidden flex flex-col"
              style={{
                opacity: 0,
                animationFillMode: "forwards",
                animationDelay: `${i * 0.1}s`,
                background: `linear-gradient(140deg, ${f.color}45 0%, #FDFAF4 60%, ${f.color}22 100%)`,
              }}
            >
              {f.badge && (
                <span
                  className="absolute top-4 right-4 text-[9px] tracking-[0.15em] uppercase font-semibold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${f.color}25`, color: f.color }}
                >
                  {f.badge}
                </span>
              )}
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                style={{ backgroundColor: `${f.color}35` }}
              >
                <f.icon size={22} strokeWidth={1.5} style={{ color: f.color }} />
              </div>
              <h3 className="font-heading text-xl text-[#5B3E2E] mb-2 leading-tight">{f.title}</h3>
              <p className="text-sm text-[#5B3E2E]/65 leading-relaxed flex-1">{f.desc}</p>
              {f.extra && (
                <p
                  className="text-[11px] leading-relaxed mt-4 pt-3 border-t border-[#5B3E2E]/10 italic"
                  style={{ color: f.color }}
                >
                  {f.extra}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div
          className="relative rounded-3xl overflow-hidden shadow-[0_12px_40px_rgba(91,62,46,0.18)] min-h-[460px] sm:min-h-[520px] flex items-end"
          style={{
            backgroundImage:
              "url(https://customer-assets-0z36b82j.emergentagent.net/job_comunidad-duelo/artifacts/iolcnn20_IMG_6699.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Warm gradient overlay for text legibility */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(91,62,46,0.85) 0%, rgba(91,62,46,0.55) 35%, rgba(91,62,46,0.15) 65%, rgba(91,62,46,0) 100%)",
            }}
          />
          <div className="relative w-full px-8 pb-10 pt-16 sm:px-12 sm:pb-14 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
              Comienza tu camino
            </h2>
            <p className="text-white/90 mb-8 max-w-md mx-auto drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]">
              El primer paso es el más importante. Únete a la comunidad Refugium.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="landing-cta-btn"
                onClick={() => navigate("/register")}
                className="bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full px-8 py-3 font-medium hover:-translate-y-0.5 transition-all shadow-[0_4px_20px_rgba(217,120,51,0.5)]"
              >
                Únete ahora
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#5B3E2E]/8 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="" className="w-6 h-6 rounded-full" />
            <span className="text-sm text-[#5B3E2E]/40">Refugium {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-6 text-xs text-[#5B3E2E]/30">
            <a href="/terms" className="hover:text-[#5B3E2E]/60 transition-colors">Términos y condiciones</a>
            <span className="mx-2 text-[#5B3E2E]/20">·</span>
            <a href="/privacy" className="hover:text-[#5B3E2E]/60 transition-colors">Política de Privacidad</a>
            <span>Un espacio seguro para todos</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
